import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Thread } from "./Thread";
import { ANDREA_PHONE, ANDREA_PHONE_DISPLAY, contactClick } from "../lib/contact";
import { getGa4Identity } from "../lib/ga4";
import { trackEvent } from "../lib/tracking";

const API = process.env.REACT_APP_BACKEND_URL;
const STAMP_KEY = "tnpc_pending_payment";

// Private post-assessment enrollment. Andrea texts/emails /pay/<slug>
// privately AFTER the free assessment, once she has recommended a program
// AND knows the child's stage. The six stage links — {virtual,in-home} ×
// {newborn,infant,toddler} — open a short, warm
// enrollment page: the URL itself is the choice (service + stage), so the
// parent never picks a program, a stage, or an intake form. "Complete
// Enrollment" stamps the visitor's GA4 identity onto the existing Stripe
// Payment Link (the real Stripe URLs live server-side only) and hands off
// to checkout. The stage also rides to the Stripe webhook inside
// client_reference_id, so the CONFIRMED payment recovers it server-side —
// the localStorage stamp below is only a convenience fallback for
// /payment-complete. The legacy base links (/pay/virtual, /pay/in-home)
// keep their original instant redirect.
//
// payment_link_open / enrollment_checkout_click are engagement events —
// never conversions. Stripe + the webhook remain the only source of truth
// for purchases, and a visit to this page never records one.

const STAGE_SLUGS = [
  "virtual-newborn", "virtual-infant", "virtual-toddler",
  "in-home-newborn", "in-home-infant", "in-home-toddler",
];
const BASE_SLUGS = ["virtual", "in-home"];

// Prices appear ONLY on these private noindex pages, because Andrea has
// already quoted them on the assessment call. They must match the two
// existing Stripe products — if a Stripe price ever changes, update here.
const PROGRAMS = {
  virtual: {
    name: "Virtual Sleep Consulting Program",
    price: "$749",
    inclusions: [
      "A personalized sleep plan, built around your child",
      "One-on-one support with me, from start to finish",
      "Guidance for naps, bedtime, and night wakings",
      "Ongoing support while you put the plan into practice",
      "Adjustments based on how your child responds",
    ],
  },
  "in-home": {
    name: "In-Home Sleep Consulting Program",
    price: "$2,549",
    inclusions: [
      "I come to you — we work in your home, in your child’s real rooms and routines",
      "Seeing bedtime as it actually happens — the details a call can’t capture",
      "A personalized sleep plan, built around your child and your family",
      "Guidance for naps, bedtime, and night wakings",
      "Ongoing support while you put the plan into practice, with adjustments made together",
    ],
  },
};

const STAGE_PHRASE = {
  newborn: "shaped around these first weeks with your new baby",
  infant: "shaped around these early months with your baby",
  toddler: "shaped around life with your toddler",
};

const STAGE_INTAKE_LABEL = {
  newborn: "Newborn Sleep Intake Form",
  infant: "Infant Sleep Intake Form",
  toddler: "Toddler Sleep Intake Form",
};

// The URL is the source of truth: /pay/virtual-toddler → virtual + toddler.
// Unknown slugs get the error state; legacy base slugs redirect instantly.
const parseSlug = (slug) => {
  if (STAGE_SLUGS.includes(slug)) {
    const i = slug.lastIndexOf("-");
    return { mode: "enroll", service: slug.slice(0, i), stage: slug.slice(i + 1) };
  }
  if (BASE_SLUGS.includes(slug)) return { mode: "redirect" };
  return { mode: "error" };
};

export const PayRedirect = ({ slug }) => {
  const reduce = useReducedMotion();
  const { mode, service, stage } = parseSlug(slug);
  const [failed, setFailed] = useState(mode === "error");
  const [leaving, setLeaving] = useState(false);

  const program = mode === "enroll" ? PROGRAMS[service] : null;

  // Every /pay page is private: noindex, never in nav/footer/sitemap.
  useEffect(() => {
    document.title =
      mode === "enroll"
        ? "Your enrollment — The Nurtured Parent Co."
        : "The Nurtured Parent Co. — Pediatric Sleep Consulting";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex,follow";
    document.head.appendChild(meta);
    window.scrollTo(0, 0);
    return () => {
      document.head.removeChild(meta);
      document.title = "The Nurtured Parent Co. — Pediatric Sleep Consulting";
    };
  }, [mode]);

  // Reads the visitor's GA4 identity, asks the backend for the stamped
  // Payment Link, remembers which link Andrea sent (service + stage,
  // resolved server-side — never trusted from the URL alone at the other
  // end), and leaves for Stripe. "replace" for the legacy instant
  // redirect; "assign" for the enrollment CTA so Back returns here.
  const stampAndGo = async (nav) => {
    const { clientId, sessionId } = await getGa4Identity();
    const qs = new URLSearchParams({ mode: "json" });
    if (clientId) qs.set("cid", clientId);
    if (sessionId) qs.set("sid", sessionId);
    const resp = await fetch(`${API}/api/pay/${encodeURIComponent(slug)}?${qs}`);
    if (!resp.ok) throw new Error(`pay link lookup failed: ${resp.status}`);
    const data = await resp.json();
    if (!data.redirect) throw new Error("no redirect");
    try {
      localStorage.setItem(
        STAMP_KEY,
        JSON.stringify({
          slug,
          service: data.service_type || null,
          stage: data.client_stage || null,
          ts: Date.now(),
        })
      );
    } catch {
      /* private mode — the welcome page simply stays generic */
    }
    if (nav === "assign") {
      window.location.assign(data.redirect);
    } else {
      window.location.replace(data.redirect);
    }
  };

  // Legacy base links: unchanged instant hand-off to Stripe.
  useEffect(() => {
    trackEvent("payment_link_open", { package: slug });
    if (mode !== "redirect") return;
    let cancelled = false;
    (async () => {
      try {
        await stampAndGo("replace");
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, mode]);

  const onEnroll = async () => {
    if (leaving) return;
    setLeaving(true);
    trackEvent("enrollment_checkout_click", { package: slug });
    try {
      await stampAndGo("assign");
    } catch {
      setLeaving(false);
      setFailed(true);
    }
  };

  const rise = (delay) => ({
    initial: reduce ? false : { opacity: 0, y: 22 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] },
  });

  const errorBlock = (
    <main className="min-h-svh flex items-center justify-center px-6 bg-ivory text-ink">
      <div className="max-w-md text-center">
        <div data-testid="pay-redirect-error">
          <p className="font-serif font-light text-2xl sm:text-3xl text-ink mb-4">
            This link isn&rsquo;t quite working.
          </p>
          <p className="text-base text-smoke mb-6">
            No charge was made. Text Andrea and she&rsquo;ll send you a fresh link right away.
          </p>
          <a
            data-testid="pay-redirect-text-andrea"
            href={`sms:${ANDREA_PHONE}`}
            onClick={(e) => {
              trackEvent("text_andrea_click", { location: "pay_redirect" });
              contactClick("text")(e);
            }}
            className="inline-block rounded-full bg-ink text-ivory px-6 py-3 text-sm tracking-wide hover:bg-espresso transition-colors duration-300"
          >
            Text Andrea — {ANDREA_PHONE_DISPLAY}
          </a>
        </div>
      </div>
    </main>
  );

  if (failed) return errorBlock;

  if (mode === "redirect") {
    return (
      <main className="min-h-svh flex items-center justify-center px-6 bg-ivory text-ink">
        <div className="max-w-md text-center">
          <div data-testid="pay-redirect-status" role="status">
            <p className="text-xs uppercase tracking-[0.25em] text-smoke mb-4">
              The Nurtured Parent Co.
            </p>
            <p className="font-serif font-light text-2xl sm:text-3xl text-ink">
              One moment — opening your secure checkout…
            </p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <div data-testid="enrollment-page" className="min-h-svh bg-ivory text-ink flex flex-col">
      <header className="max-w-[1600px] w-full mx-auto px-6 md:px-12 lg:px-20 py-8">
        <span data-testid="enrollment-brand" className="inline-block">
          <span className="block font-serif font-light text-xl sm:text-2xl leading-tight tracking-tight">
            The Nurtured Parent Co.
          </span>
          <span className="block mt-1 text-[9px] tracking-[0.3em] uppercase text-smoke">
            Pediatric Sleep Consulting
          </span>
        </span>
      </header>

      <main className="flex-1 max-w-[1600px] w-full mx-auto px-6 md:px-12 lg:px-20">
        {/* Reconnecting to the conversation they just had with Andrea */}
        <section className="py-16 lg:py-24">
          <motion.p {...rise(0.1)} className="text-xs tracking-[0.25em] uppercase font-semibold text-euca">
            Private enrollment
          </motion.p>

          <motion.h1 {...rise(0.25)} className="mt-6 font-serif font-light text-ink text-5xl sm:text-7xl leading-[1.02] tracking-tight">
            Ready when <span className="italic text-clay">you are.</span>
          </motion.h1>

          <motion.div {...rise(0.4)} className="mt-10 max-w-lg space-y-5 text-base sm:text-lg font-normal leading-relaxed text-smoke">
            <p>
              I&rsquo;m glad we had a chance to talk about what&rsquo;s been
              happening with your little one.
            </p>
            <p>
              Based on what we talked through, we&rsquo;ll be working together
              through my {program.name} — {STAGE_PHRASE[stage]}.
            </p>
            <p>
              Once you complete your enrollment, I&rsquo;ll have you fill out a
              more detailed intake so I can dig deeper into your child&rsquo;s
              sleep, routines, and everything we talked about today.
            </p>
          </motion.div>

          <motion.p {...rise(0.5)} className="mt-10 font-serif italic font-light text-clay text-2xl sm:text-3xl">
            — Andrea
          </motion.p>
        </section>

        {/* The program Andrea recommended — the only one shown, never a choice */}
        <section className="py-16 lg:py-20 border-t border-ink/10">
          <motion.div {...rise(0.1)} className="max-w-2xl">
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-euca">
              Your program
            </p>
            <h2 data-testid="enrollment-program-name" className="mt-6 font-serif font-light text-ink text-3xl sm:text-4xl leading-[1.12] tracking-tight">
              {program.name}
            </h2>
            <ul data-testid="enrollment-inclusions" className="mt-10 max-w-xl border-y border-ink/10 divide-y divide-ink/10">
              {program.inclusions.map((item) => (
                <li key={item} className="py-4 text-base sm:text-lg font-normal leading-relaxed text-smoke">
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>
        </section>

        {/* What happens after payment — so nothing feels uncertain */}
        <section className="py-16 lg:py-20 border-t border-ink/10">
          <motion.div {...rise(0.1)} className="max-w-2xl">
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-euca">
              What happens next
            </p>
            <ol className="mt-10 max-w-xl border-y border-ink/10 divide-y divide-ink/10">
              <li className="py-5 flex gap-6">
                <span className="font-serif italic font-light text-clay text-xl sm:text-2xl leading-snug shrink-0">01</span>
                <p className="text-base sm:text-lg font-normal leading-relaxed text-smoke">
                  Complete your enrollment below — it takes about a minute.
                </p>
              </li>
              <li className="py-5 flex gap-6">
                <span className="font-serif italic font-light text-clay text-xl sm:text-2xl leading-snug shrink-0">02</span>
                <p className="text-base sm:text-lg font-normal leading-relaxed text-smoke">
                  You&rsquo;ll land on a welcome page with your{" "}
                  <span className="text-ink">{STAGE_INTAKE_LABEL[stage]}</span> —
                  that&rsquo;s where you tell me more about your child&rsquo;s
                  sleep, routines, and what&rsquo;s been happening at home.
                </p>
              </li>
              <li className="py-5 flex gap-6">
                <span className="font-serif italic font-light text-clay text-xl sm:text-2xl leading-snug shrink-0">03</span>
                <p className="text-base sm:text-lg font-normal leading-relaxed text-smoke">
                  From there, I get to work on your plan — and we begin.
                </p>
              </li>
            </ol>

            <button
              data-testid="enrollment-checkout-cta"
              onClick={onEnroll}
              disabled={leaving}
              className="mt-12 w-full sm:w-auto justify-center inline-flex items-center gap-3 rounded-full bg-ink text-ivory font-medium px-10 py-5 text-lg hover:bg-espresso focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clay focus-visible:ring-offset-2 focus-visible:ring-offset-ivory disabled:opacity-60 transition-colors duration-300 group"
            >
              {leaving ? (
                "Opening secure checkout…"
              ) : (
                <>
                  Complete Enrollment — {program.price}
                  <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                </>
              )}
            </button>
            <p className="mt-4 text-sm font-normal text-smoke">
              Secure checkout via Stripe.
            </p>

            <p className="mt-10 text-base font-normal leading-relaxed text-smoke">
              Have a question before you start?{" "}
              <a
                data-testid="enrollment-text-andrea"
                href={`sms:${ANDREA_PHONE}`}
                onClick={(e) => {
                  trackEvent("text_andrea_click", { location: `enrollment_${slug}` });
                  contactClick("text")(e);
                }}
                className="text-ink underline decoration-clay/50 underline-offset-4 hover:decoration-clay transition-colors duration-300"
              >
                Text Andrea
              </a>
              .
            </p>
          </motion.div>
        </section>
      </main>

      <div className="max-w-[1600px] w-full mx-auto px-6 md:px-12 lg:px-20 pb-10">
        <Thread className="text-clay/40" />
      </div>
    </div>
  );
};
